"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { userSettingsAPI } from "@/lib/api"
import { useToast } from "@/components/ui/use-toast"

interface UserSetting {
  id: string
  configurator_type: string
  price_multiplier: number
  enabled: boolean
}

interface UserSettingsContextType {
  settings: UserSetting[]
  loading: boolean
  error: string | null
  refreshSettings: () => Promise<void>
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined)

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [settings, setSettings] = useState<UserSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userSettingsAPI.getSettings()
      setSettings(response.settings)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to load user settings"
      setError(errorMessage)
      console.error("Error fetching user settings:", err)
    } finally {
      setLoading(false)
    }
  }

  const refreshSettings = async () => {
    await fetchSettings()
  }

  useEffect(() => {
    const unauthenticatedPaths = ["/login", "/reset-password", "/register"]
    const isUnauthPath = unauthenticatedPaths.some(path => pathname.startsWith(path))
    if (isUnauthPath) {
      // Skip fetching to avoid 401 errors when user is not authenticated
      setLoading(false)
      return
    }
    fetchSettings()
  }, [pathname])

  return (
    <UserSettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings,
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  )
}

export function useUserSettings() {
  const context = useContext(UserSettingsContext)
  if (context === undefined) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider")
  }
  return context
} 